'use strict';
/* ==========================================================================
   levels/march.js — step "march" (any): THE MARCH, "Nobody Gets All the Power" as a paper-collage parade with
   karaoke (DESIGN.md §2, §5.4).

   ► The song: assets/music/march.m4a = ACE-Step take march_alt_7 (Sousa-style march, 120 bpm, 84 s; assets/CHOICE.md).
     Timings: assets/music/march_words.json (tools/song_timing.py): the TRUE lyric lines of content/song.json with
     word times from the take's Whisper passes, and the beat grid {bpm 120, offset = first downbeat ≈ 1.59 s}.

   The whole frame is a pure function of SONG TIME t (the shared <audio> element's clock while it plays; a wall clock
   while audio is locked or missing), so seeking, pausing and the QA screenshots are exact. Every picture beat is keyed
   to a sung word (S.W(line, word)); every vote number comes from core-local/civics.js (Civics.CHAMBERS.senate,
   majorityNeeded, twoThirdsNeeded).

   The parade (always on): the three branches march on the beat along a kraft-paper road, each in its colour with its
   woodtype placard (Civic.BRANCH: legislative navy + Capitol, executive brick + White House, judicial old gold +
   Court); bunting bobs on the beat. They march IN during the count-in, mark time (hop in place) while checks are
   drawn, march OFF and back ON during the bridge. Positions are stepped on the beat grid (formX).
   Acts, keyed to lyric-line indices (content/song.json order):
     intro    0      "HUP · TWO · THREE · FOUR" woodtype counts
     verse1   1–4    the bill's story: the quill writes, the scroll flies up; sign (the pen) or VETO (stamp + E→L
                     arrow); OVERRIDE 2/3 of each chamber (L→E arrow, the VETO struck); the LAW wax seal
     chorus   5–8, 15–18   NOBODY GETS / ALL THE POWER! in ransom letters word by word; MAKE IT / RUN IT / JUDGE IT
                     over the three; on "checks everywhere" every check arrow at once
     verse2   9–12   NOMINATES (E→J), SENATE CONFIRMS (L→J), HOUSE: IMPEACH / SENATE: TRIAL (L→E), STRIKE DOWN (J→L,
                     "a law that breaks the Constitution, in a real case"), then NO ONE IS ABOVE THE LAW
     bridge   13–14  the Senate as 100 paper dots: more than half = 51, two-thirds = 67 (66.67 rounds UP; 66 fails)
     outro    19–20  the hook once more; a paper crown drops and tumbles off on "king"; confetti; SIGNED + a wax seal
   Each check arrow is an ink arrow in the colour of the branch that HOLDS the check (ARROWS).
   Karaoke (bottom band, torn cream paper): the current line in woodtype, the sung word on a mustard scrap, branch
   words in their branch colour, the hook "Nobody gets all the power" in ransom letters, the next line faint below.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom;
   "Finish" once ≥ 90 % has been heard. The song's end (≥ 90 % heard) → Game.complete('march').
   Portrait phone: the stage on top, Dock.karaoke + ↺ ▶ ⌂ + the strip below.
   Long music (docs/WEBSITE.md §8.4): lesson.json preload.stream names the .m4a, so it plays through the shared
   <audio> element (Audio.media), never decoded into memory.

   QA hooks: sc.song (S(i), E(i), W(i, word), beats), sc.plan.acts, sc.debugAt(t) (freeze at song time t), sc.now(),
   sc.play(t), sc.pause(), sc.toggle(), sc.seek(t), sc.restart(), sc.done() / sc.finishStub() (stamp now),
   sc.lyricAt(t), sc.activeWord(t), sc.actAt(t), sc.formAt(t), sc.arrowsAt(t), sc.heard, sc.ended, sc.audioReady.
   ========================================================================== */
(function () {
  const STEP = 'march';
  const TAKE_NAME = 'march_alt_7';
  const AUDIO = 'music/march.m4a', WORDS = 'music/march_words.json';
  const KEY = 'song:march';
  const WOOD = "'Alfa Slab One'", HANDF = "'Caveat Brush'", TYPE = "'Special Elite'";
  const CC = () => (window.Civic ? Civic.C : { navy: '#1f2f55', brick: '#9e2b25', cream: '#f3ead7', gold: '#b08a2e', parchment: '#efe2c2', wax: '#8e1f1b', ink: '#2b2420', kraft: '#c99a67' });
  const BR = () => (window.Civic ? Civic.BRANCH : {
    legislative: { color: '#1f2f55', ink: '#f3ead7', label: 'LEGISLATIVE' },
    executive: { color: '#9e2b25', ink: '#f3ead7', label: 'EXECUTIVE' },
    judicial: { color: '#b08a2e', ink: '#2b2420', label: 'JUDICIAL' },
  });
  const GOLD_TEXT = '#86651a';                       // old gold, dark enough to read as text on cream
  const BAND_Y = 832, CUR_Y = 912, NEXT_Y = 1012, CUR_SIZE = 56, NEXT_SIZE = 36;
  const ROAD_Y = 760;                                 // the buildings' base line
  const FADE = 0.35;                                  // act cross-fade (s)
  const THRESH = 0.9;                                 // share of the song heard for the stamp / "Finish"
  const CREDIT = 'AI-generated song · the civics is real';

  /* the parade: order, spacing from the formation's centre, heights chosen so the three read the same width */
  const TROOP = [
    { b: 'legislative', kind: 'capitol', dx: -560, h: 250, seed: 11 },
    { b: 'executive', kind: 'whitehouse', dx: 0, h: 180, seed: 12 },
    { b: 'judicial', kind: 'court', dx: 560, h: 215, seed: 13 },
  ];
  const TOP = TROOP.map(r => ROAD_Y - r.h);

  /* check arrows (formation-local x; y absolute): a → b, bowed UP by h (the Bezier's control offset) */
  const SLOT = {
    EL:  { from: 'executive', a: [-110, 575], b: [-470, 505], h: 200 },     // veto
    LE:  { from: 'legislative', a: [-440, 490], b: [-150, 560], h: 400 },   // override
    LE2: { from: 'legislative', a: [-420, 500], b: [-70, 572], h: 290 },    // impeach → trial
    EJ:  { from: 'executive', a: [110, 575], b: [430, 545], h: 220 },       // nominate
    LJ:  { from: 'legislative', a: [-480, 490], b: [560, 505], h: 640 },    // confirm
    JL:  { from: 'judicial', a: [470, 520], b: [-590, 478], h: 480 },       // strike down
  };
  Object.values(SLOT).forEach(s => {
    const [ax, ay] = s.a, [bx, by] = s.b, dx = bx - ax, dy = by - ay, L = Math.hypot(dx, dy);
    let nx = dy / L, ny = -dx / L; if (ny > 0) { nx = -nx; ny = -ny; }
    s.c = [(ax + bx) / 2 + nx * s.h, (ay + by) / 2 + ny * s.h];
    s.peak = [0.25 * ax + 0.5 * s.c[0] + 0.25 * bx, 0.25 * ay + 0.5 * s.c[1] + 0.25 * by];
  });

  /* ---------------- song timing ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  class Song {
    constructor(d) {
      this.d = d; this.lines = d.lines; this.dur = d.duration || 84;
      const b = d.beats || {};
      this.bpm = b.bpm || 120; this.bt = 60 / this.bpm; this.off = b.offset || 0;
      this.items = d.lines.map(l => ({ ...l, kind: 'line' })).concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j }))).sort((a, c) => a.s - c.s);
    }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) return 1e6;
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    /* the count-in ("Hup, two, three, four") is on the grid too, so the pulse runs from 0 */
    pulse(t, k = 9) { return Math.exp(-this.sinceBeat(t) * k); }
    singing(t) { return this.lines.some(l => t >= l.s - 0.05 && t <= l.e + 0.1); }
  }

  /* ---------------- the vote numbers (the model only) ---------------- */
  let NUMS = null;
  function nums() {
    if (NUMS) return NUMS;
    const Cv = window.Civics;
    const N = Cv.CHAMBERS.senate, maj = Cv.majorityNeeded(N), two = Cv.twoThirdsNeeded(N);
    const exact = Math.round(200 * N / 3) / 100;                 // 2/3 of N to two decimals (66.67)
    NUMS = { N, maj, two, exact: exact.toFixed(2), under: two - 1, underFails: Cv.overrideFails(two - 1, N - two + 1), twoOk: Cv.overrides(two, N - two) };
    return NUMS;
  }

  /* ---------------- the plan: acts, formation waypoints, check arrows ---------------- */
  function plan(S) {
    const b = i => S.S(i) - 0.3;
    const acts = [
      { id: 'intro', at: 0, draw: actIntro },
      { id: 'verse1', at: b(1), draw: actVerse1 },
      { id: 'chorus1', at: S.W(5, 'Nobody') - 0.05, draw: actChorus, c0: 5 },
      { id: 'verse2', at: b(9), draw: actVerse2 },
      { id: 'bridge', at: S.W(13, 'than') - 0.2, draw: actBridge },     // after the verse's last word has had its moment
      { id: 'chorus2', at: S.W(15, 'Nobody') - 0.05, draw: actChorus, c0: 15 },   // the bridge's 67 stays up to the last beat
      { id: 'outro', at: b(19), draw: actOutro },
    ];
    acts.forEach((a, i) => { a.end = i + 1 < acts.length ? acts[i + 1].at : S.dur + 1; });
    // the formation's centre x: march in (count-in), hold, march off right + back in from the left (bridge), hold
    const tOff = S.S(14), tBack = S.S(15) - 0.3;
    const way = [[0, -420], [S.S(1) - 0.1, 960], [S.S(13), 960], [tOff, 2620], [tOff, -720], [tBack, 960], [S.dur + 5, 960]];
    // the checks, each keyed to its word; `v` = the verse window (arrows dim after it), labels on while `on`
    const W = (i, w, n) => S.W(i, w, n);
    const checks = [
      { id: 'veto', slot: 'EL', t0: W(2, 'veto'), label: 'VETO', grp: 1, v: [b(1), b(5)] },
      { id: 'override', slot: 'LE', t0: W(3, 'override'), label: 'OVERRIDE', sub: '2/3 of each chamber', grp: 1, v: [b(1), b(5)] },
      { id: 'nominate', slot: 'EJ', t0: W(9, 'picks'), label: 'NOMINATES', sub: 'judges', grp: 2, v: [b(9), S.S(12) - 0.2] },
      { id: 'confirm', slot: 'LJ', t0: W(9, 'agree'), label: 'SENATE CONFIRMS', grp: 2, v: [b(9), S.S(12) - 0.2] },
      { id: 'impeach', slot: 'LE2', t0: W(10, 'impeach'), label: 'HOUSE: IMPEACH', label2: 'SENATE: TRIAL', t2: W(10, 'trial'), grp: 3, v: [b(9), S.S(12) - 0.2] },
      { id: 'strike', slot: 'JL', t0: W(11, 'strike'), label: 'STRIKE DOWN', sub: 'if it breaks the Constitution (in a real case)', t1: W(11, 'Constitution'), grp: 4, v: [b(9), S.S(12) - 0.2] },
    ];
    const everywhere = [5, 15].map(c => ({ t0: W(c + 3, 'checks'), end: b(c + 4) }));
    return { acts, ticks: acts.slice(1).map(a => a.at), way, checks, everywhere, tStamp: S.E(20) + 0.9, tKing: W(20, 'king') };
  }
  function actAt(p, t) { let k = 0; for (let i = 0; i < p.acts.length; i++) if (t >= p.acts[i].at) k = i; return k; }

  /* the formation on the beat grid: it moves only in the first ~60 % of each beat (a step), then plants */
  function formAt(S, p, t) {
    const bp = S.beatPos(t), fl = Math.floor(bp), fr = bp - fl, step = clamp(fr * 1.7);
    const tq = S.off + (fl + E.inOutSine(step)) * S.bt;
    const w = p.way;
    let cx = w[w.length - 1][1], moving = false;
    for (let i = 0; i + 1 < w.length; i++) {
      const [ta, xa] = w[i], [tb, xb] = w[i + 1];
      if (tq >= ta && tq < tb && tb > ta) { cx = lerp(xa, xb, (tq - ta) / (tb - ta)); moving = xa !== xb; break; }
      if (tq < w[0][0]) { cx = w[0][1]; break; }
    }
    const lift = Math.sin(Math.PI * step), side = ((fl % 2) + 2) % 2 ? 1 : -1;
    const hop = (moving ? 26 : 12) * lift, rot = side * (moving ? 0.06 : 0.035) * lift;
    return { cx, hop, rot, moving, beat: fl };
  }

  /* the arrows showing at t: [{slot, col, prog, alpha, label?, …}] */
  function arrowsAt(S, p, t) {
    const out = [];
    const ev = p.everywhere.find(e => t >= e.t0 - 0.05 && t < e.end);
    if (ev) {
      const order = ['EL', 'LE', 'EJ', 'LJ', 'LE2', 'JL'];
      order.forEach((k, i) => {
        const t0 = ev.t0 + i * 0.12;
        if (t >= t0) out.push({ slot: k, prog: E.outCubic(P(t, t0, t0 + 0.4)), alpha: 1 - P(t, ev.end - 0.3, ev.end) });
      });
      return out;
    }
    for (const c of p.checks) {
      if (t < c.t0 || t >= c.v[1] + 0.4) continue;
      const dim = t >= c.v[1] ? 1 - P(t, c.v[1], c.v[1] + 0.4) : 1;
      // a newer check (a later group of the same verse) takes the stage: this one's label goes, its arrow steps back
      const nx = p.checks.filter(o => o.grp > c.grp && o.v[0] === c.v[0]).reduce((m, o) => Math.min(m, o.t0), 1e9);
      const back = P(t, nx, nx + 0.3);
      out.push({ ...c, prog: E.outCubic(P(t, c.t0, c.t0 + 0.45)), alpha: dim * (1 - 0.55 * back), labelA: dim * (1 - back) });
    }
    return out;
  }

  /* ---------------- drawing helpers ---------------- */
  const layoutCache = new Map();
  const fontOk = () => !document.fonts || document.fonts.status === 'loaded';
  function measure(str, font) {
    const k = font + '|' + str;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  function ransomW(str, size, seed) {
    const k = 'R|' + str + '|' + size + '|' + seed;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); w = ransom(str, -9999, -9999, size, 1e9, { seed }); g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  /* the engine's ink() sets globalAlpha itself: pass the current (faded) alpha through */
  const inkA = (pts, o = {}) => ink(pts, { ...o, alpha: (o.alpha ?? 1) * g.globalAlpha });
  function withAlpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  /* a quadratic ink arrow a → c → b drawn on with prog */
  function qArrow(a, c, b, { color, w = 8, prog = 1, seed = 1, head = 30 } = {}) {
    const pts = [];
    for (let i = 0; i <= 22; i++) { const u = i / 22; pts.push([(1 - u) * (1 - u) * a[0] + 2 * (1 - u) * u * c[0] + u * u * b[0], (1 - u) * (1 - u) * a[1] + 2 * (1 - u) * u * c[1] + u * u * b[1]]); }
    inkA(pts, { w, color, seed, prog: clamp(prog * 1.2), passes: 2, step: 14, amp: 1.2 });
    const hp = clamp((prog - 0.8) / 0.2);
    if (hp > 0) {
      const ang = Math.atan2(b[1] - c[1], b[0] - c[0]);
      inkA([[b[0] - Math.cos(ang - 0.45) * head, b[1] - Math.sin(ang - 0.45) * head], b, [b[0] - Math.cos(ang + 0.45) * head, b[1] - Math.sin(ang + 0.45) * head]], { w, color, seed: seed + 5, prog: hp, step: 8, passes: 2 });
    }
  }
  function wood(str, x, y, size, t0, o = {}) {
    if (!window.Civic) { ransom(str, x, y, size, t0, { seed: o.seed || 1 }); return; }
    Civic.woodtype(str, x, y, size, { t0, ...o });
  }
  function branchWood(b, str, x, y, size, t0, seed) { const B = BR()[b]; wood(str, x, y, size, t0, { color: B.color, ink: B.ink, seed, stagger: 0.03 }); }

  /* ---------------- the stage: background, bunting, road, the parade ---------------- */
  function backdrop(S, t, docked) {
    const C = CC();
    g.fillStyle = paperTile(C.parchment, 6); g.fillRect(0, 0, 1920, 1080);
    // bunting: bobs on the beat (the pennants are cached sprites; only the transform moves)
    const pu = S.pulse(t, 8), x1 = docked ? 1890 : Math.min(1580, UI.hudLeft(3) - 30);
    if (window.Civic) {
      g.save(); g.translate(0, 8 * pu);
      Civic.bunting(docked ? 30 : 470, x1, 4, { sag: docked ? 12 : 22, n: docked ? 17 : 11, seed: 3, sway: 1 + pu });   // shallow: the top arrow's label sits under it
      g.restore();
    }
    // the road
    paperRect(960, ROAD_Y + 26, 2080, 120, C.kraft, { seed: 21, torn: true, depth: 1.1, boil: false, tex: 'tex_kraft' });
    if (docked) paperRect(960, 1010, 2080, 150, C.cream, { seed: 22, torn: true, depth: 0.8, boil: false });
    // road dashes, sliding with the formation so the road reads as travelled
    const F = formAt(S, S._plan, t), off = ((F.cx % 240) + 240) % 240;
    g.save(); g.fillStyle = 'rgba(80,54,30,0.35)';
    for (let x = -240 + off; x < 1960; x += 240) g.fillRect(x, ROAD_Y + 52, 90, 7);
    g.restore();
  }
  /* emphasis when a branch is named (a quick grow) */
  const EMPH = [
    [0, 1, 'Congress'], [0, 1, 'House'], [0, 1, 'Senate'], [0, 3, 'chamber'], [0, 9, 'Senate'], [0, 10, 'House'], [0, 10, 'Senate'],
    [1, 2, 'President'], [1, 9, 'President'],
    [2, 9, 'judges'], [2, 11, 'courts'],
    [0, 6, 'Make'], [1, 6, 'run'], [2, 6, 'judge'], [0, 16, 'Make'], [1, 16, 'run'], [2, 16, 'judge'],
  ];
  function emph(S, i, t) {
    let e = 0;
    for (const [k, l, w] of EMPH) if (k === i) { const tw = S.W(l, w); if (t >= tw && t < tw + 1) e = Math.max(e, Math.exp(-(t - tw) * 5)); }
    return e;
  }
  function parade(S, p, t) {
    const F = formAt(S, p, t);
    TROOP.forEach((r, i) => {
      const x = F.cx + r.dx;
      if (x < -420 || x > 2340) return;
      const s = 1 + 0.12 * emph(S, i, t);
      g.save(); g.translate(x, ROAD_Y - F.hop); g.rotate(F.rot); g.scale(s, s);
      if (window.Civic) Civic.building(r.kind, 0, 0, r.h, { seed: r.seed });
      branchWood(r.b, BR()[r.b].label, 0, 36, 26, -1e6, 70 + i);
      g.restore();
    });
    return F;
  }
  /* the check arrows, in formation-local x */
  function drawArrows(S, p, t, F) {
    const list = arrowsAt(S, p, t);
    if (!list.length) return;
    g.save(); g.translate(F.cx, 0);
    list.forEach((c, j) => {
      const s = SLOT[c.slot], B = BR()[s.from];
      withAlpha(c.alpha, () => qArrow(s.a, s.c, s.b, { color: B.color, prog: c.prog, seed: 300 + j * 7 }));
    });
    // labels on top of every arrow
    list.forEach((c, j) => {
      if (!c.label || c.labelA <= 0.01) return;
      const s = SLOT[c.slot], [px, py] = s.peak, lt = c.t0 + 0.25;
      withAlpha(c.labelA, () => {
        const ly = py - 30;
        if (c.label2 && t >= c.t2) {
          branchWood(s.from, c.label, px, ly - 44, 28, lt, 500 + j);
          wood(c.label2, px, ly, 28, c.t2, { color: BR().legislative.color, ink: BR().legislative.ink, seed: 520 + j, stagger: 0.03 });
        } else branchWood(s.from, c.label, px, ly, 28, lt, 500 + j);
        if (c.sub && t > (c.t1 || c.t0) + 0.3) hand(c.sub, px, ly - 42, 32, (c.t1 || c.t0) + 0.3, { color: CC().ink, seed: 540 + j, dur: 0.4 });
      });
    });
    g.restore();
  }

  /* ======================================================================
     the acts (overlays on the parade)
     ====================================================================== */
  function actIntro(S, t) {
    const l = S.lines[0]; if (!l) return;
    const ws = l.words, labels = ['HUP!', 'TWO', 'THREE', 'FOUR'];
    let k = -1; for (let j = 0; j < ws.length; j++) if (t >= ws[j].s) k = j;
    if (k < 0) { ransom('THE MARCH', 960, 300, 110, 0, { seed: 42 }); return; }
    const w = ws[k], s = 1 + 0.15 * Math.exp(-(t - w.s) * 8);
    g.save(); g.translate(960, 320); g.scale(s, s);
    wood(labels[k] || w.w.toUpperCase(), 0, 0, 120, w.s, { color: CC().cream, ink: CC().navy, seed: 60 + k, stagger: 0.02 });
    g.restore();
  }

  /* verse 1: the bill's trip — written, sign or veto, override, LAW */
  const SCROLL = [1010, 270];
  function actVerse1(S, t, A, F) {
    const C = CC(), L = [F.cx + TROOP[0].dx, TOP[0]];
    const tWr = S.W(1, 'writes'), tBi = S.W(1, 'bills'), tHo = S.W(1, 'House'), tSe = S.W(1, 'Senate');
    const tSi = S.W(2, 'sign'), tVe = S.W(2, 'veto'), tOv = S.W(3, 'override'), tLaw = S.W(4, 'law');
    // the quill writes over the Capitol, then the scroll flies up to centre stage
    if (t >= tWr && window.Civic) {
      const a = 1 - P(t, tBi + 0.4, tBi + 0.8), wig = Math.sin((t - tWr) * 22) * 0.12 * (t < tBi ? 1 : 0);
      withAlpha(a, () => Civic.tool('quill', L[0] + 120, L[1] - 60, 110, { rot: -0.4 + wig, s: pop(T, tWr, 0.3), seed: 81 }));
    }
    if (t >= tBi) {
      const u = E.inOutCubic(P(t, tBi, tBi + 0.6));
      const x = lerp(L[0] + 60, SCROLL[0], u), y = lerp(L[1] - 30, SCROLL[1], u), s = lerp(0.5, 1, u) * (1 + 0.08 * Math.exp(-(t - S.W(4, 'bill')) * 6) * (t > S.W(4, 'bill') ? 1 : 0));
      if (IMG.scroll) cut('scroll', x, y, 120, { s, rot: -0.05, seed: 82 });
      else window.Civic && Civic.parchment(x, y, 250 * s, 110 * s, { seed: 82, rot: -0.05 });
      if (u >= 1) hand('the bill', SCROLL[0], SCROLL[1] + 88, 34, tBi + 0.6, { color: C.ink, seed: 83 });
    }
    // HOUSE / SENATE: the Capitol's two chambers
    if (t >= tHo) branchWood('legislative', 'HOUSE', L[0] - 120, ROAD_Y - 120 - F.hop, 24, tHo, 84);
    if (t >= tSe) branchWood('legislative', 'SENATE', L[0] + 120, ROAD_Y - 120 - F.hop, 24, tSe, 85);
    // sign (the pen) … or veto
    if (t >= tSi && window.Civic) Civic.tool('pen', SCROLL[0] + 190, SCROLL[1] - 10, 100, { rot: 0.5, s: pop(T, tSi, 0.3), seed: 86 });
    if (t >= tVe) {
      UI.stamp('VETO', SCROLL[0], SCROLL[1] + 4, { rot: -0.14, s: pop(T, tVe, 0.3, 2) * 0.5, color: C.brick });
      if (t >= tOv) inkA([[SCROLL[0] - 110, SCROLL[1] + 30], [SCROLL[0] + 110, SCROLL[1] - 20]], { w: 9, color: C.navy, seed: 87, prog: E.outCubic(P(t, tOv + 0.1, tOv + 0.4)) });
    }
    if (t >= tLaw && window.Civic) Civic.seal(SCROLL[0] + 118, SCROLL[1] + 36, 50, { text: 'LAW', s: pop(T, tLaw, 0.35, 2.4), rot: 0.1, seed: 88 });
  }

  /* the chorus: NOBODY GETS / ALL THE POWER! word by word; MAKE IT / RUN IT / JUDGE IT; checks everywhere */
  const HOOK_ROWS = [[0, 1], [2, 3, 4]];
  const HOOK_TXT = ['NOBODY', 'GETS', 'ALL', 'THE', 'POWER!'];
  function hookRansom(S, t, lineIdx, rows, y0, size, seedBase, alpha = 1) {
    const l = S.lines[lineIdx]; if (!l) return;
    const ws = l.words, pu = S.pulse(t, 7);
    withAlpha(alpha, () => rows.forEach((row, r) => {
      const gap = size * 0.45, wsz = row.map(k => ransomW(HOOK_TXT[k], size, seedBase + k));
      const tot = wsz.reduce((a, b) => a + b, 0) + gap * (row.length - 1);
      let x = 960 - tot / 2;
      row.forEach((k, j) => {
        const w = ws[k], cx = x + wsz[j] / 2; x += wsz[j] + gap;
        if (!w || t < w.s) return;
        const s = 1 + 0.05 * pu + 0.12 * Math.exp(-(t - w.s) * 7);
        g.save(); g.translate(cx, y0 + r * size * 1.35); g.scale(s, s);
        ransom(HOOK_TXT[k], 0, 0, size, w.s, { seed: seedBase + k, stagger: 0.025 });
        g.restore();
      });
    }));
  }
  function actChorus(S, t, A, F) {
    const c0 = A.c0, tChecks = S.W(c0 + 3, 'checks');
    // the hook, on its two lines; the second time it's sung the letters re-pop
    const second = t >= S.S(c0 + 2) - 0.1;
    const hA = 1 - P(t, tChecks - 0.5, tChecks - 0.1);
    hookRansom(S, t, second ? c0 + 2 : c0, HOOK_ROWS, 200, 92, second ? 620 : 600, hA);
    // MAKE IT / RUN IT / JUDGE IT over the three (line c0+1), bouncing on "fair"
    const tFair = S.W(c0 + 1, 'fair'), lA = 1 - P(t, tChecks - 0.5, tChecks - 0.1);
    [['Make', 'MAKE IT'], ['run', 'RUN IT'], ['judge', 'JUDGE IT']].forEach(([w, txt], i) => {
      const t0 = S.W(c0 + 1, w); if (t < t0) return;
      const r = TROOP[i], bounce = t > tFair ? 1 + 0.12 * Math.exp(-(t - tFair) * 5) * Math.abs(Math.sin((t - tFair) * 9)) : 1;
      withAlpha(lA, () => {
        g.save(); g.translate(F.cx + r.dx, TOP[i] - 52 - F.hop); g.scale(bounce, bounce);
        branchWood(r.b, txt, 0, 0, 40, t0, 640 + i);
        g.restore();
      });
    });
    // "checks everywhere": the arrows are drawn by drawArrows (plan.everywhere)
  }

  /* verse 2: nominate / confirm, impeach / trial, strike down; then nobody is above the law */
  function actVerse2(S, t, A, F) {
    const C = CC(), J = [F.cx + TROOP[2].dx, TOP[2]];
    const tStr = S.W(11, 'strike'), tCo = S.W(11, 'Constitution'), tNo = S.W(12, 'no'), tSc = S.W(12, 'school');
    // the gavel raps over the Court on "strike"
    if (t >= tStr - 0.1 && t < S.S(12) + 0.2 && window.Civic) {
      const rap = Math.max(0, Math.sin((t - tStr) * 14)) * Math.exp(-(t - tStr) * 2);
      withAlpha(1 - P(t, S.S(12) - 0.2, S.S(12) + 0.2), () => Civic.tool('gavel', J[0] + 170, J[1] - 40 - F.hop, 110, { rot: -0.3 - 0.5 * rap, s: pop(T, tStr - 0.1, 0.3), seed: 91 }));
    }
    // the Constitution card
    if (t >= tCo - 0.2 && window.Civic) {
      const a = 1 - P(t, S.S(12) - 0.2, S.S(12) + 0.2);
      withAlpha(a, () => {
        const s = pop(T, tCo - 0.2, 0.35);
        Civic.parchment(1640, 250, 440, 130, { seed: 92, rot: 0.04, s, lines: 2 });
        if (s > 0.9) hand('the Constitution', 1640, 250, 40, tCo, { color: C.ink, seed: 93, font: TYPE, rot: 0.04, dur: 0.4 });
      });
    }
    // NO ONE IS ABOVE THE LAW (line 12)
    if (t >= tNo - 0.1) {
      wood('NO ONE IS', 960, 250, 64, tNo - 0.1, { color: C.cream, ink: C.navy, seed: 94, stagger: 0.03 });
      wood('ABOVE THE LAW', 960, 350, 64, S.W(12, 'above') - 0.1, { color: C.cream, ink: C.brick, seed: 95, stagger: 0.03 });
      if (t >= tSc) hand('(not even the class president)', 960, 446, 44, tSc, { color: C.brick, seed: 96, dur: 0.5 });
    }
  }

  /* the bridge: the Senate as 100 dots; more than half = 51, two-thirds = 67 */
  function actBridge(S, t) {
    const C = CC(), n = nums(), N = n.N;
    const tHalf = S.W(13, 'half'), tTwo = S.W(13, 'twothirds'), tAll = S.W(14, 'All'), tSix = S.W(14, 'Sixtyseven'), tSide = S.W(14, 'side');
    // the Senate as 5 rows of 20 dots, on a card top left (kept above the buildings' tops, y < 500)
    const cols = 20, rows = Math.ceil(N / cols), gp = 30, x0 = 170, y0 = 250;
    let fill = 0;
    if (t >= tHalf) fill = Math.round(n.maj * E.outCubic(P(t, tHalf, tHalf + 0.9)));
    if (t >= tTwo) fill = n.maj + Math.round((n.two - n.maj) * E.outCubic(P(t, tTwo, tTwo + 0.8)));
    const pA = E.outCubic(P(t, S.S(13) - 0.2, S.S(13) + 0.3)), mx = x0 + (cols - 1) * gp / 2;
    withAlpha(pA, () => {
      window.Civic && Civic.parchment(mx, y0 + (rows - 1) * gp / 2 - 6, cols * gp + 60, rows * gp + 100, { seed: 97, rot: -0.01 });
      hand(`the Senate: ${N} votes`, mx, y0 - 44, 32, S.S(13) - 0.2, { color: C.ink, seed: 98, font: TYPE, dur: 0.3 });
      g.save(); g.lineWidth = 2;
      const ring = t >= tAll ? Math.exp(-(t - tAll) * 3) : 0;
      for (let i = 0; i < N; i++) {
        const cx = x0 + (i % cols) * gp, cy = y0 + Math.floor(i / cols) * gp;
        g.beginPath(); g.arc(cx, cy, 11 + 2.5 * ring, 0, TAU);
        g.fillStyle = i < fill ? (i < n.maj ? C.navy : '#3d5a8a') : C.cream; g.fill();
        g.strokeStyle = 'rgba(43,36,32,0.55)'; g.stroke();
      }
      g.restore();
      // the two-thirds mark: a brick bar right after vote n.two
      if (t >= tTwo + 0.8) {
        const r = Math.floor((n.two - 1) / cols), c = (n.two - 1) % cols, xx = x0 + c * gp + gp / 2, yy = y0 + r * gp;
        inkA([[xx, yy - gp * 0.7], [xx, yy + gp * 0.7]], { w: 6, color: C.brick, seed: 99, prog: E.outCubic(P(t, tTwo + 0.8, tTwo + 1.1)) });
      }
    });
    const TX = 860;
    if (t >= S.W(13, 'More') - 0.1) {
      branchWood('legislative', 'PASS', TX + 70, 205, 40, S.W(13, 'More') - 0.1, 110);
      if (t >= tHalf) hand(`more than half: ${n.maj} of ${N}`, TX + 180, 205, 50, tHalf, { align: 'left', color: C.ink, seed: 111, dur: 0.5 });
    }
    if (t >= tTwo - 0.1) {
      branchWood('legislative', 'OVERRIDE', TX + 118, 300, 40, tTwo - 0.1, 112);
      hand(`two-thirds: ${n.two} of ${N}`, TX + 290, 300, 50, tTwo, { align: 'left', color: C.ink, seed: 113, dur: 0.5 });
    }
    if (t >= tAll) hand(`all ${N} voting?`, TX + 20, 400, 44, tAll, { align: 'left', color: C.navy, seed: 114, dur: 0.4 });
    if (t >= tSix) {
      const s = 1 + 0.1 * Math.exp(-(t - tSix) * 6);
      g.save(); g.translate(TX + 390, 400); g.scale(s, s);
      wood(String(n.two), 0, 0, 80, tSix, { color: C.cream, ink: C.navy, seed: 115, stagger: 0.05 });
      g.restore();
      hand(`2/3 of ${N} = ${n.exact}, so round UP`, TX + 20, 478, 42, tSix + 0.3, { align: 'left', color: C.ink, seed: 116, dur: 0.5 });
    }
    if (t >= tSide) hand(`${n.under} is not enough`, TX + 500, 400, 42, tSide, { align: 'left', color: C.brick, seed: 117, dur: 0.4 });
  }

  /* the outro: the hook once more; a paper crown drops and tumbles off on "king"; confetti; SIGNED */
  const CROWN = Object.assign([[-70, 34], [70, 34], [72, -30], [40, 0], [22, -46], [0, -8], [-22, -46], [-40, 0], [-72, -30]], { k: 'march:crown' });
  function actOutro(S, t, A, F) {
    const C = CC();
    // "NOBODY GETS ALL THE POWER" on one row
    hookRansom(S, t, 19, [[0, 1, 2, 3, 4]], 170, 72, 680, 1 - P(t, S.S(20) - 0.2, S.S(20) + 0.2));
    // the crown
    const tNot = S.W(20, 'Not'), tKing = S.W(20, 'king');
    if (t >= tNot - 0.2) {
      let x = 960, y, rot = 0;
      y = lerp(-80, 330, E.outBack(P(t, tNot - 0.2, tNot + 0.5), 1.4));
      if (t >= tKing) {
        const u = P(t, tKing, tKing + 1.1);
        x = lerp(960, 1245, u); y = 330 + (ROAD_Y + 26 - 330) * u * u - 160 * Math.sin(Math.PI * u) * (1 - u);   // lands on the road, between the White House and the Court
        rot = lerp(0, Math.PI - 0.12, E.outCubic(u));                                                                // … upside down
      }
      paperPiece(CROWN, x, y, C.gold, { rot, s: 1.2, seed: 131, depth: 1.2, outline: true });
      if (t < tKing) [[-46, 12], [0, 18], [46, 12]].forEach(([dx, dy], i) => paperCircle(x + dx * 1.2, y + dy * 1.2, 7, [C.brick, C.navy, C.brick][i], { seed: 132 + i, depth: 0 }));
    }
    // confetti + SIGNED
    if (t >= tKing) confetti(t, tKing);
    const tS = A.tStamp;
    if (t >= tS) {
      window.Civic && Civic.seal(1220, 420, 62, { s: pop(T, tS + 0.25, 0.35, 2.4), rot: 0.2, seed: 141 });
      UI.stamp('SIGNED', 960, 380, { rot: -0.1, s: pop(T, tS, 0.3, 2.2) * 1.3, color: C.brick });
    }
  }
  function confetti(t, t0) {
    const C = CC(), cols = [C.navy, C.brick, C.gold, C.cream, PAL.white], r = rng(771);
    g.save();
    for (let i = 0; i < 90; i++) {
      const x0 = r() * 1920, delay = r() * 1.4, v = 260 + r() * 260, spin = (r() - 0.5) * 10, sw = 20 + r() * 40, ph = r() * TAU, col = cols[i % cols.length];
      const w = 14 + r() * 12, h = 8 + r() * 8, dt = t - t0 - delay;
      if (dt < 0) continue;
      const y = -30 + v * dt, x = x0 + Math.sin(dt * 3 + ph) * sw;
      if (y > 1100) continue;
      g.save(); g.translate(x, y); g.rotate(spin * dt); g.scale(1, Math.cos(dt * 5 + ph));
      g.fillStyle = col; g.fillRect(-w / 2, -h / 2, w, h); g.strokeStyle = 'rgba(43,36,32,0.35)'; g.lineWidth = 1.5; g.strokeRect(-w / 2, -h / 2, w, h);
      g.restore();
    }
    g.restore();
  }

  function drawStage(S, p, t, docked) {
    backdrop(S, t, docked);
    const F = parade(S, p, t);
    const k = actAt(p, t), A = p.acts[k];
    const run = (i, a) => withAlpha(a, () => { g.save(); p.acts[i].draw(S, t, { ...p.acts[i], tStamp: p.tStamp }, F); g.restore(); });
    if (k > 0 && t < A.at + FADE) run(k - 1, 1 - P(t, A.at, A.at + FADE));
    run(k, k === 0 ? 1 : P(t, A.at, A.at + FADE * 0.6));
    drawArrows(S, p, t, F);
    return F;
  }

  /* ---------------- karaoke ---------------- */
  const HOOK = ['nobody', 'gets', 'all', 'the', 'power'];
  const BRANCH_WORD = { congress: 'legislative', house: 'legislative', senate: 'legislative', chamber: 'legislative',
    president: 'executive', courts: 'judicial', judges: 'judicial' };
  const wordColor = w => { const b = BRANCH_WORD[normW(w)]; return !b ? null : b === 'judicial' ? GOLD_TEXT : BR()[b].color; };
  function hookIdx(item) {
    const ws = item.words, out = new Set();
    for (let k = 0; k + HOOK.length <= ws.length; k++) if (HOOK.every((w, j) => normW(ws[k + j].w) === w)) HOOK.forEach((_, j) => out.add(k + j));
    return out;
  }
  function tokens(item) {
    const ws = item.words, hk = hookIdx(item), toks = [];
    for (let k = 0; k < ws.length; k++) {
      if (hk.has(k) && !hk.has(k - 1)) {
        const run = ws.slice(k, k + HOOK.length), last = run[run.length - 1];
        const trail = (last.w.match(/[,.!?]+$/) || [''])[0];
        toks.push({ text: run.map(w => w.w).join(' ').replace(/[,.!?]+$/, '').toUpperCase(), s: run[0].s, e: last.e, ransom: true, trail });
        k += HOOK.length - 1; continue;
      }
      toks.push({ text: ws[k].w, s: ws[k].s, e: ws[k].e, col: wordColor(ws[k].w) });
    }
    return toks;
  }
  const lays = new Map();
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (lays.has(key)) return lays.get(key);
    const toks = tokens(item), f = `${size}px ${WOOD}`, space = measure(' ', f) + size * 0.08, rs = size * 0.62;
    let x = 0;
    toks.forEach((tk, j) => {
      tk.seed = 400 + item.i * 13 + j * 5;
      if (tk.ransom) {
        tk.w = ransomW(tk.text, rs, tk.seed); tk.tw = tk.trail ? measure(tk.trail, f) : 0;
        tk.x = x + size * 0.1; x += tk.w + tk.tw + size * 0.2 + space;
      } else { tk.w = measure(tk.text, f); tk.x = x; x += tk.w + space; }
    });
    const out = { toks, width: x - space, size, rs };
    if (fontOk()) lays.set(key, out);
    return out;
  }
  const tokEnd = (toks, j) => { const tk = toks[j], nx = toks[j + 1]; return Math.min(Math.max(tk.e, tk.s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  function drawCurrent(item, t, y, maxW = 1780) {
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 960 - Lo.width / 2, C = CC();
    const appear = item.appear ?? item.s - 0.5, a = E.outCubic(P(t, appear, appear + 0.25)), yy = y + (1 - a) * 36;
    const f = `${size}px ${WOOD}`;
    Lo.toks.forEach((tk, j) => {
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < tokEnd(Lo.toks, j);
      if (tk.ransom) {
        if (!sung) withAlpha(0.3 * a, () => { g.font = `${Math.round(size * 0.8)}px ${TYPE}`; g.fillStyle = C.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(tk.text, x + tk.w / 2, yy); });
        else {
          const beat = 1 + 0.06 * Math.exp(-(t - tk.s) * 6);
          g.save(); g.translate(x + tk.w / 2, yy); g.scale(beat, beat);
          ransom(tk.text, 0, 0, Lo.rs, tk.s, { seed: tk.seed, stagger: clamp((tk.e - tk.s) / Math.max(1, tk.text.length), 0.02, 0.06) });
          g.restore();
        }
        if (tk.trail && sung) { g.save(); g.font = f; g.fillStyle = C.ink; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(tk.trail, x + tk.w + 6, yy); g.restore(); }
        return;
      }
      if (active) paperRect(x + tk.w / 2, yy + size * 0.04, tk.w + 24, size * 1.08, PAL.mustard, { seed: tk.seed, depth: 0.5, boil: false, s: 1 + 0.06 * Math.exp(-(t - tk.s) * 10) });
      g.save(); g.font = f; g.textAlign = 'left'; g.textBaseline = 'middle';
      if (!sung) { g.globalAlpha *= 0.3 * a; g.fillStyle = C.ink; g.fillText(tk.text, x, yy); }
      else {
        const p = E.outQuad(P(t, tk.s, tk.s + clamp(tk.e - tk.s, 0.08, 0.3)));
        if (p < 1) { g.save(); g.globalAlpha *= 0.3; g.fillStyle = C.ink; g.fillText(tk.text, x, yy); g.restore(); g.beginPath(); g.rect(x - 4, yy - size, (tk.w + 8) * p, size * 2); g.clip(); }
        g.fillStyle = tk.col || C.ink; g.fillText(tk.text, x, yy);
      }
      g.restore();
    });
  }
  /* a line comes up 0.5 s before its first word, but never before the previous line has been sung */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.5 : Math.min(it[k].s, Math.max(it[k].s - 0.5, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) { const nx = ws[j + 1], end = Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); if (t >= ws[j].s - 0.02 && t < end) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e }; }
    return null;
  }
  function band(song, t) {
    const C = CC();
    paperRect(960, BAND_Y + 140, 2040, 290, C.cream, { seed: 77, torn: true, depth: 1.8, boil: false });
    tape(110, BAND_Y + 6, 150, -0.08, undefined, 71); tape(1810, BAND_Y + 6, 150, 0.08, undefined, 72);
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) { g.save(); g.font = `28px ${TYPE}`; g.fillStyle = 'rgba(31,47,85,0.6)'; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(sec.toLowerCase(), 70, BAND_Y + 36); g.restore(); }
    if (cur) drawCurrent(cur, t, CUR_Y);
    const note = (txt, col, a) => { let size = NEXT_SIZE; const f = s => `${s}px ${TYPE}`; const w = measure(txt, f(size)); if (w > 1700) size = Math.floor(size * 1700 / w);
      g.save(); g.globalAlpha *= a; g.font = f(size); g.fillStyle = col; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(txt, 960, NEXT_Y); g.restore(); };
    if (next) note(next.text, C.navy, 0.6);
    else if (song.items.length && t > song.items[song.items.length - 1].e) note('Make it, run it, judge it fair.', C.navy, 0.75);
    if (!cur && !next && t > 1) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      g.save(); g.font = `72px 'Segoe UI Symbol', 'DejaVu Sans', sans-serif`; g.fillStyle = C.navy; g.textAlign = 'center'; g.textBaseline = 'middle';
      ['♪', '♫', '♪'].forEach((n, i) => g.fillText(n, 860 + i * 100, CUR_Y - 24 * pu * (i === b ? 1 : 0.15)));
      g.restore();
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class March extends Scene {
    enter() {
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0; this.lastT = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      try { nums(); } catch (e) { this.err = 'civics model: ' + e.message; console.warn('[march]', this.err); }
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { this.song = new Song(d); this.plan = plan(this.song); this.song._plan = this.plan; })
        .catch(e => { this.err = String(e && e.message); console.warn('[march] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); });
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.lastT = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.lastT = this.pos; this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; this.lastT = t; }
    /* stamp the step now (the end of a ≥ 90 % listen, the "Finish" button, QA) */
    done() { if (this.completed) return; this.completed = true; if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    finishStub() { this.done(); }
    canFinish() { return !!this.song && !this.completed && this.heard >= THRESH * this.song.dur; }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    actAt(t) { return this.plan ? this.plan.acts[actAt(this.plan, t)].id : null; }
    formAt(t) { return this.plan ? formAt(this.song, this.plan, t) : null; }
    arrowsAt(t) { return this.plan ? arrowsAt(this.song, this.plan, t).map(a => ({ slot: a.slot, id: a.id || null, from: SLOT[a.slot].from, label: a.label || null, prog: +a.prog.toFixed(2), alpha: +a.alpha.toFixed(2) })) : []; }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      const now = this.now();
      if (this.lastT < this.plan.tStamp && now >= this.plan.tStamp) Audio.play('stamp', { gain: 0.6 });
      this.lastT = now;
      if (now >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= THRESH * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) Game.replace(new Levels.map());
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this.finishRect && Math.abs(x - this.finishRect[0]) < this.finishRect[2] / 2 && Math.abs(y - this.finishRect[1]) < this.finishRect[3] / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null; this.lastT = this.pos;
      if (was) this.play(this.pos);
    }

    draw() {
      const docked = UI.docked(), title = (Game.content.song && Game.content.song.title) || 'Nobody Gets All the Power';
      if (!this.song || this.err) {
        g.fillStyle = paperTile(CC().parchment, 6); g.fillRect(0, 0, 1920, 1080);
        ransom('THE MARCH', 960, 400, 110, this.enteredAt + 0.2, { seed: 42 });
        const msg = this.err ? 'the song is missing (' + this.err + ')' : 'tuning the brass…';
        hand(msg, 960, 580, 54, this.enteredAt + 0.4, { color: CC().navy, seed: 44 });
        if (docked) { Dock.head(title, ''); Dock.text('mr-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try {
        drawStage(this.song, this.plan, t, docked);
        if (!docked) band(this.song, t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t, title); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) {
          const hk = hookIdx(cur);
          this._dk = { key, words: cur.words.map((w, j) => ({ w: w.w, s: w.s, e: w.e, hook: hk.has(j) })) };
        }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? 'Make it, run it, judge it fair.' : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the song…' });
      Dock.text('credit', ['♪ ' + CREDIT], { cls: 'small center' });
      if (this.ended && Game.current() === this) {
        Dock.buttons('mr-end', [{ label: '↺ Play it again', color: PAL.teal, onClick: () => this.restart() },
          { label: 'Back to the map', color: CC().navy, onClick: () => Game.replace(new Levels.map()) }]);
      } else if (this.canFinish() && Game.current() === this) {
        Dock.buttons('mr-finish', [{ label: 'Finish', color: CC().brick, onClick: () => this.done() }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'song position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150, 1080);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the song from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => Game.replace(new Levels.map()) });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos; this.lastT = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    /* the paused ▶: a dim wash and a mustard paper disc */
    pausedMark(y, r, h) {
      g.save(); g.fillStyle = 'rgba(27,21,18,0.28)'; g.fillRect(0, 0, 1920, h); g.restore();
      paperCircle(960, y, r, PAL.mustard, { seed: 63, depth: 1.6 });
      g.save(); g.fillStyle = PAL.ink; g.beginPath(); g.moveTo(960 - r * 0.32, y - r * 0.55); g.lineTo(960 + r * 0.55, y); g.lineTo(960 - r * 0.32, y + r * 0.55); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const t0 = this.enteredAt, S = this.song, C = CC();
      ransom('THE MARCH', 40, 56, 36, t0 + 0.1, { align: 'left', seed: 61 });
      hand('♪ ' + CREDIT, 44, 114, 28, t0 + 0.4, { align: 'left', color: C.navy, seed: 62, dur: 0.4 });
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx = { r: m.r, fixed: true };
      if (UI.iconButton('mr-map', '⌂', m.x, m.y, fx)) { Game.replace(new Levels.map()); return; }
      if (S) {
        if (UI.iconButton('mr-restart', '↺', r.x, r.y, fx)) this.restart();
        if (UI.iconButton('mr-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx, color: PAL.mustard })) this.toggle();
      }
      if (t == null) return;
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('mr-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 22 : 12, sy = 1080 - sh;
      g.save();
      g.fillStyle = 'rgba(43,36,32,0.25)'; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = C.brick; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = C.navy; this.plan.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) paperCircle(clamp(1920 * t / S.dur, 26, 1894), touch ? 1080 - 24 : sy + sh / 2, touch ? 22 : 14, C.navy, { seed: 66, depth: 0.8, boil: false });
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        this.pausedMark(430, 100, BAND_Y);
        hand(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, 48, -1e6, { dur: 0.01, color: PAL.cream, outline: 'rgba(31,47,85,0.92)', seed: 64 });
      }
      if (!this.startedOnce && !this.frozen) hand('loading the song…', 960, 590, 48, this.enteredAt + 0.3, { color: C.navy, seed: 65 });
      this.finishRect = null;
      if (this.ended && Game.current() === this) {
        if (UI.button('mr-again', '↺ Play it again', 700, 640, { w: 440, h: 110, color: PAL.teal, size: 56 })) this.restart();
        if (UI.button('mr-back', 'Back to the map', 1220, 640, { w: 440, h: 110, color: C.navy, size: 56 })) Game.replace(new Levels.map());
      } else if (this.canFinish() && Game.current() === this) {
        // top right, under the HUD buttons: clear of the three branch labels on the ground strip (JUDICIAL) and the lyrics
        const bw = 300, bh = UI.tap(90), bx = 1920 - 40 - bw / 2, by = m.y + m.r + 28 + bh / 2;
        this.finishRect = [bx, by, bw, bh];
        if (UI.button('mr-finish', 'Finish', bx, by, { w: bw, h: 90, color: C.brick, size: 52 })) this.done();
      }
    }
  }
  March.STEP = STEP;
  March.TAKE = TAKE_NAME;
  March.Song = Song;
  March.plan = plan;
  March.nums = nums;
  Levels.march = March;
})();
